import { Injectable } from '@angular/core'

import { Bet, CalculationBet } from '../../pages/my-bets/types/my-bets.type'
import { AsianHandicapService } from '../components/asian-handicap-pnl/asian-handicap.service'
import { DebuggingService } from '../components/debugging-modal/services/debugging.service'
import { BalanceData } from '../components/header/types/balance-data.type'
import { Bet as RiskBet } from '../types/bet.type'
import { MarketProvider, PredictedBet } from '../types/market.type'
import { BetService } from './bet.service'
import { HelperService } from './helper.service'
import { RiskExposureService } from './risk-exposure.service'

@Injectable({
	providedIn: 'root',
})
export class ExposureService {
	constructor(
		private readonly helperService: HelperService,
		private readonly betService: BetService,
		private readonly debuggingService: DebuggingService,
		private readonly asianHandicapService: AsianHandicapService,
		private readonly riskExposureService: RiskExposureService,
	) {}

	/**
	 * Validates a predicted bet against the user's current balance and existing bets.
	 * Separates bets into respective categories to evaluate their exposure for different market types.
	 * It will return an insufficient balance validation object if the predicted bet causes a negative balance after placement.
	 *
	 * @param {CalculationBet[]} matchedNonPendingBets - The user's existing matched non-pending bets excluding the current bet.
	 * @param {BalanceData} balanceData - The user's balance information to compute betting availability.
	 * @param {PredictedBet} predictedBet - The new bet being validated for potential negative balance issue.
	 * @param {string} currentBetId - The unique identifier for the current bet being placed or updated.
	 * @param {boolean} [shouldDebug=true] - Optional flag to enable or disable debugging information for the validation.
	 * @return {{ insufficientBalance: boolean } | null} Returns an object indicating insufficient balance if the bet's potential loss exceeds available balance or null if validation passes.
	 */
	validate(
		matchedNonPendingBets: CalculationBet[],
		balanceData: BalanceData,
		predictedBet: PredictedBet,
		currentBetId: string,
		shouldDebug = true,
	):
		| {
				insufficientBalance: boolean
		  }
		| {
				cancelUnmatchedBetsFirst: boolean
		  }
		| null {
		const debuggingKey =
			`${this.betService.betsMarkets$.getValue()[predictedBet.marketId]?.event?.name} - ${this.betService.betsMarkets$.getValue()[predictedBet.marketId]?.marketName} - ${currentBetId}`.toUpperCase()
		// this.debuggingService.exposureDebuggingObject = {}
		const oddsExposuresStepOne: Record<string, number> = {} // Will hold exposure values for ODDS based markets. Key is unique ID (marketId for odds markets and topic for bookmaker markets)
		const asianHandicapExposuresStepOne: Record<string, number> = {} // Will hold exposure values for AsianHandicap based markets. Key is market ID
		const goalLinesExposuresStepOne: Record<string, number> = {} // Will hold exposure values for Goal-Line based markets. Key is market ID
		const doubleChanceExposuresStepOne: Record<string, number> = {} // Will hold exposure values for ODDS based markets. Key is unique ID (marketId for odds markets and topic for bookmaker markets)
		const lineExposuresStepOne: Record<string, number> = {} // Will hold exposure values for LINE based markets. Key is <marketId>-<selectionId>

		// Get list of the user's open bets excluding the bet that is being updated (this applies only when we call this function during bet update)
		const matchedNonPendingBetsWithoutCurrentBet = matchedNonPendingBets.filter((bet) => bet.betId !== currentBetId)

		// Separate ODDS and LINE bets
		const oddsBets: CalculationBet[] = []
		const asianHandicapBets: CalculationBet[] = []
		const goalLinesBets: CalculationBet[] = []
		const doubleChanceBets: CalculationBet[] = []
		const lineBets: CalculationBet[] = []

		matchedNonPendingBetsWithoutCurrentBet.forEach((bet) => {
			if (this.helperService.isAsianHandicapBet(bet)) {
				asianHandicapBets.push(bet)
			} else if (this.helperService.isGoalLinesBet(bet)) {
				goalLinesBets.push(bet)
			} else if (this.helperService.isLineBet(bet)) {
				lineBets.push(bet)
			} else if (this.helperService.isDoubleChanceBet(bet)) {
				doubleChanceBets.push(bet)
			} else {
				oddsBets.push(bet)
			}
		})

		// Insert the predicted bet in the appropriate group
		if (this.helperService.isAsianHandicapBet(predictedBet)) {
			asianHandicapBets.push(this.betService.enhancePredictedBetToCalculationBet(predictedBet))
		} else if (this.helperService.isGoalLinesBet(predictedBet)) {
			goalLinesBets.push(this.betService.enhancePredictedBetToCalculationBet(predictedBet))
		} else if (this.helperService.isLineBet(predictedBet)) {
			lineBets.push(this.betService.enhancePredictedBetToCalculationBet(predictedBet))
		} else if (this.helperService.isDoubleChanceBet(predictedBet)) {
			doubleChanceBets.push(this.betService.enhancePredictedBetToCalculationBet(predictedBet))
		} else {
			oddsBets.push(this.betService.enhancePredictedBetToCalculationBet(predictedBet))
		}

		// const oddsBetsExcludeUnmatched = [...oddsBets].filter((bet) => {
		// 	const isMatched = this.betService.unmatchedBets.some((unmatchedBet) => unmatchedBet.betId === bet.betId)
		// 	return !isMatched
		// })

		// Get unique IDs from the bets
		const oddsMarketsMarketGroupIds = Array.from(new Set(oddsBets.map((bet) => bet.marketGroupId)))
		const asianHandicapMarketsMarketGroupIds = Array.from(new Set(asianHandicapBets.map((bet) => bet.marketGroupId)))
		const goalLinesMarketsMarketGroupIds = Array.from(new Set(goalLinesBets.map((bet) => bet.marketGroupId)))
		const doubleChanceMarketsMarketGroupIds = Array.from(new Set(doubleChanceBets.map((bet) => bet.marketGroupId)))
		const lineMarketsUniqueIds = Array.from(new Set(lineBets.map((bet) => `${bet.marketId}-${bet.selectionId}`)))

		// For each unique IDs, calculate and store the exposure result
		oddsMarketsMarketGroupIds.forEach(
			(marketGroupId) => (oddsExposuresStepOne[marketGroupId] = this.calculateOddsAndLineMarketBasedExposure(oddsBets, marketGroupId)),
		)
		asianHandicapMarketsMarketGroupIds.forEach(
			(marketGroupId) => (asianHandicapExposuresStepOne[marketGroupId] = this.calculateAsianHandicapBasedExposure(asianHandicapBets, marketGroupId)),
		)
		goalLinesMarketsMarketGroupIds.forEach(
			(marketGroupId) => (goalLinesExposuresStepOne[marketGroupId] = this.calculateGoalLinesBasedExposure(goalLinesBets, marketGroupId)),
		)
		doubleChanceMarketsMarketGroupIds.forEach(
			(marketGroupId) =>
				(doubleChanceExposuresStepOne[marketGroupId] = this.calculateDoubleChanceBasedExposure(
					doubleChanceBets.filter((bet) => bet.marketGroupId === marketGroupId),
				)),
		)
		lineMarketsUniqueIds.forEach((uniqueId) => (lineExposuresStepOne[uniqueId] = this.calculateRunnerBasedExposure(lineBets, uniqueId)))

		// Sum all the exposures got from the step above (This will be the total exposure)
		const oddsTotalExposure = Object.values(oddsExposuresStepOne).reduce((prev, curr) => prev + curr, 0)
		const asianHandicapTotalExposure = Object.values(asianHandicapExposuresStepOne).reduce((prev, curr) => prev + curr, 0)
		const goalLinesTotalExposure = Object.values(goalLinesExposuresStepOne).reduce((prev, curr) => prev + curr, 0)
		const doubleChanceTotalExposure = Object.values(doubleChanceExposuresStepOne).reduce((prev, curr) => prev + curr, 0)
		const lineTotalExposure = Object.values(lineExposuresStepOne).reduce((prev, curr) => prev + curr, 0)

		// const unMatchedBetsExposure: number = this.betService.unmatchedNonPendingBets$.getValue().reduce((acc, bet) => {
		// 	return  acc + +bet.potentialLoss
		// },0);
		// console.log(unMatchedBetsExposure)

		const newTotalExposure = oddsTotalExposure + lineTotalExposure + doubleChanceTotalExposure + asianHandicapTotalExposure + goalLinesTotalExposure
		// const potentialLoss = this.betService.enhancePredictedBetToCalculationBet(predictedBet).potentialLoss
		const exposureWhenUnmatched = Math.abs(+balanceData.TOTAL_EXPOSURE)
		const maxExposure = predictedBet.side === 'LAY' ? Math.max(newTotalExposure, exposureWhenUnmatched) : newTotalExposure
		console.log('Max: ', newTotalExposure, maxExposure)

		let worstExposure = 0
		const unmatchedBets: CalculationBet[] = []

		try {
			unmatchedBets.push(
				...this.betService.unmatchedBets$
					.getValue()
					.filter((bet) => bet.betId !== currentBetId)
					.map((bet) => ({
						...bet,
						marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
						rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
					})),
			)

			const allBetsWithPredictBet = [
				...matchedNonPendingBetsWithoutCurrentBet,
				...unmatchedBets,
				this.betService.enhancePredictedBetToCalculationBet(predictedBet),
			]
			// const doubleChanceBets = allBetsWithPredictBet.filter((bet) => this.helperService.isDoubleChanceBet(bet))
			// const asianHandicapBets = allBetsWithPredictBet.filter(bet => this.isAsianHandicap(bet))
			// const goalLineBets = allBetsWithPredictBet.filter((bet) => this.helperService.isGoalLinesBet(bet))

			const matchedOddBets = allBetsWithPredictBet.filter((bet) => {
				return (
					!this.helperService.isDoubleChanceBet(bet) &&
					!this.helperService.isAsianHandicapBet(bet) &&
					!this.helperService.isGoalLinesBet(bet) &&
					!this.helperService.isLineBet(bet)
				)
			})

			const allBets = matchedOddBets.map((bet) => ({
				betId: bet.betId,
				marketId: bet.marketId,
				selectionId: bet.selectionId.toString(),
				side: bet.side.toLowerCase() as 'back' | 'lay',
				price: bet.betId === '-1' ? Number(bet.fOdds) : Number(bet.averagePriceMatched) || Number(bet.fOdds) || 1.01,
				betSize: Number(bet.betSize),
				stakeMatched: Number(bet.stakeMatched),
			}))
			// console.log("allBets: ", allBets, unmatchedBets);
			const betModels = allBets.map((betDto) => RiskBet.fromDto(betDto))
			const marketSelectionIds: Map<string, string[]> = this.betService.getMarketSelectionIds(betModels)
			worstExposure = this.riskExposureService.calculateSumOfIndividualMarketExposures(betModels, marketSelectionIds)

			worstExposure += doubleChanceTotalExposure + asianHandicapTotalExposure + goalLinesTotalExposure + lineTotalExposure
			// console.log("worstExposure: ", worstExposure)
		} catch (error) {
			console.error('❌ Risk exposure calculation error:', error)
		}

		if (shouldDebug) {
			let newMarketExposure
			if (this.helperService.isAsianHandicapBet(predictedBet)) {
				newMarketExposure = asianHandicapExposuresStepOne[predictedBet.marketGroupId]
			} else if (this.helperService.isGoalLinesBet(predictedBet)) {
				newMarketExposure = goalLinesExposuresStepOne[predictedBet.marketGroupId]
			} else if (this.helperService.isLineBet(predictedBet)) {
				newMarketExposure = lineExposuresStepOne[`${predictedBet.marketId}-${predictedBet.selectionId}`]
			} else if (this.helperService.isDoubleChanceBet(predictedBet)) {
				newMarketExposure = doubleChanceExposuresStepOne[predictedBet.marketGroupId]
			} else {
				newMarketExposure = oddsExposuresStepOne[predictedBet.marketGroupId]
			}
			this.debuggingService.exposureDebuggingObject[debuggingKey] = {
				newTotalExposure: worstExposure,
				newMarketExposure,
				exposureWhenUnmatched,
			}
		}

		// Step 0
		// NEW LOGIC: Check unmatched LAY bets for this market and compare exposures
		const unmatchedBetsForMarket = this.betService.unmatchedNonPendingBets$
			.getValue()
			.filter((bet) => this.helperService.generateMarketGroupIdForCalculationBet(bet) === predictedBet.marketGroupId)
		const betsForThisMarket = oddsBets.filter((bet) => bet.marketGroupId === predictedBet.marketGroupId)
		const betsWithoutPredicted = betsForThisMarket.slice(0, -1)

		const oldMarketExposure = this.calculateOddsAndLineMarketBasedExposure(betsWithoutPredicted, predictedBet.marketGroupId)
		//
		// // Calculate this market's exposure with predicted bet (current oddsBets already includes it)
		const newMarketExposure = this.calculateOddsAndLineMarketBasedExposure(betsForThisMarket, predictedBet.marketGroupId)

		/**
		 * If Predicted bet is Lay bet && unmatchedBackBet > 0
		 *   const consideredMatchedBet = assume all unmatched back bet as matched bet + all matched bet
		 *   const worstExposure = calculateExposure(allUnmatchedLayBet + predictedBet, consideredMatchedBet)
		 *   if(worstExposure > balance) {
		 *       {insufficientBalance: true}
		 *   }
		 *
		 */
		// Get all matched and unmatched bets
		const allMatchedBets = this.betService.matchedNonPendingBets$.getValue().filter((bet) => bet.betId !== currentBetId)
		const allUnmatchedBets = this.betService.unmatchedNonPendingBets$.getValue().filter((bet) => bet.betId !== currentBetId)

		// Convert to CalculationBet format
		const matchedCalculationBets = allMatchedBets.map((bet) => this.enhanceBetToCalculationBet(bet, bet.stakeMatched || 0))
		const unmatchedCalculationBets = allUnmatchedBets.map((bet) => this.enhanceBetToCalculationBet(bet, bet.betSize))

		// Separate unmatched bets by side
		const unmatchedBackBets = unmatchedCalculationBets.filter((bet) => bet.side === 'BACK')
		const unmatchedLayBets = unmatchedCalculationBets.filter((bet) => bet.side === 'LAY')

		if (predictedBet.side === 'LAY' && unmatchedBackBets.length > 0) {
			const consideredMatchedBets = [...matchedCalculationBets, ...unmatchedBackBets]
			const worstCaseUnmatchedBets = [...unmatchedLayBets, this.betService.enhancePredictedBetToCalculationBet(predictedBet)]

			if (this.checkWorstCaseExposure(worstCaseUnmatchedBets, consideredMatchedBets, balanceData)) {
				console.log('step 0.2')
				return { insufficientBalance: true }
			}
		}

		/**
		 * If Predicted bet is Back bet && unmatchedLayBet > 0
		 *   const consideredMatchedBet = assume all unmatched lay bet as matched bet + all matched bet
		 *   const worstExposure = calculateExposure(allUnmatchedBackBet + predictedBet, consideredMatchedBet)
		 *   if(worstExposure > balance) {
		 *       {insufficientBalance: true}
		 *   }
		 *
		 */
		if (predictedBet.side === 'BACK' && unmatchedLayBets.length > 0) {
			const consideredMatchedBets = [...matchedCalculationBets, ...unmatchedLayBets]
			const worstCaseUnmatchedBets = [...unmatchedBackBets, this.betService.enhancePredictedBetToCalculationBet(predictedBet)]

			if (this.checkWorstCaseExposure(worstCaseUnmatchedBets, consideredMatchedBets, balanceData)) {
				console.log('step 0.3')
				return { insufficientBalance: true }
			}
		}

		let cancelUnmatchedBet = false
		if (newMarketExposure < oldMarketExposure) {
			if (unmatchedBetsForMarket.length === 0) {
				return null
			}
			cancelUnmatchedBet = true
		}

		// if (+balanceData.BALANCE <= exposureWhenUnmatched) {
		// 	return { insufficientBalance: true }
		// }

		if (Math.abs(worstExposure) > +balanceData.BALANCE) {
			if (cancelUnmatchedBet) {
				return { insufficientBalance: true }
			}
			return { insufficientBalance: true }
		} else {
			if (worstExposure > +balanceData.BALANCE) {
				return { insufficientBalance: true }
			}
			return null
		}

		// Step 0
		// Calculate total exposure with the predicted bet. If the total exposure(from the balance) > calculated total exposure, return null.

		// Calculate unmatched bets across all markets
		// const unmatchedBetsForMarket = this.betService.unmatchedNonPendingBets$
		// 	.getValue()
		// 	.filter((bet) => this.helperService.generateMarketGroupIdForCalculationBet(bet) === predictedBet.marketGroupId)
		// const allUnmatchedBets = this.betService.unmatchedNonPendingBets$.getValue()

		// Sum of potential losses from all unmatched BACK bets across all markets
		// const unmatchedBackExposure = allUnmatchedBets
		// 	.filter((bet) => bet.side === 'BACK' || bet.side === 'LAY')
		// 	.reduce((sum, bet) => sum + Number(bet.potentialLoss || bet.betSize), 0)

		// Count of unmatched LAY bets across all markets
		// const noUnmatchedLayBets = unmatchedBetsForMarket.filter((bet) => bet.side === 'LAY').length
		//
		// if (noUnmatchedLayBets === 0 && predictedBet.side === 'LAY') {
		// 	if (Math.abs(Number(balanceData.TOTAL_EXPOSURE)) > newTotalExposure + unmatchedBackExposure) {
		// 		return null
		// 	}
		// }

		// Step 1
		// Check if the bet is a LAY bet on a BF ODDS market and if the available balance is less than the market exposure
		// if (predictedBet.side === 'LAY' && predictedBet.provider === MarketProvider.BF && predictedBet.bettingType !== 'LINE') {
		// 	// const oddsBetsExposure = this.calculateOddsAndLineMarketBasedExposure(oddsBets, predictedBet.marketGroupId)
		// 	// const doubleChanceExposure = this.calculateDoubleChanceBasedExposure(doubleChanceBets)
		// 	// const marketExposure = oddsBetsExposure + doubleChanceExposure
		// 	//
		// 	// const availableToBet = this.getAvailableToBet(balanceData)
		// 	// if (availableToBet < marketExposure) return { insufficientBalance: true }
		//
		// 	const availableToBet = this.getAvailableToBet(balanceData)
		// 	const betExposure = this.betService.calculatePotentialLoss(
		// 		Number(predictedBet.stake),
		// 		Number(predictedBet.fOdds),
		// 		predictedBet.side,
		// 		0,
		// 		0,
		// 		predictedBet.bettingType,
		// 	)
		// 	if (availableToBet < betExposure) return { insufficientBalance: true }
		// }

		// Step 2
		// If validation fails, return the error
		// const validation = this.checkInsufficientBalance(newTotalExposure, balanceData)
		// if (validation) {
		// 	return validation
		// }
		//
		// // Step 3
		// const oddsExposuresStepThree: Record<string, number> = {} // Will hold exposure values for ODDS based markets. Key is unique ID (marketId for odds markets and topic for bookmaker markets)
		// const asianHandicapExposuresStepThree: Record<string, number> = {} // Will hold exposure values for AsianHandicap based markets. Key is market ID
		// const goalLinesExposuresStepThree: Record<string, number> = {} // Will hold exposure values for Goal-Line based markets. Key is market ID
		// const doubleChanceExposuresStepThree: Record<string, number> = {} // Will hold exposure values for double-chance markets. Key is unique ID (marketId for odds markets and topic for bookmaker markets)
		// const lineExposuresStepThree: Record<string, number> = {} // Will hold exposure values for LINE based markets. Key is <marketId>-<selectionId>
		// const matchedBets: CalculationBet[] = []
		// const unmatchedBets: CalculationBet[] = []

		// // Divide partially matched bets into their matched and unmatched parts
		// this.betService.partiallyMatchedBets$.getValue().forEach((bet) => {
		// 	const split = this.betService.splitPartiallyMatchedBet(bet)
		// 	matchedBets.push(split[0])
		// 	unmatchedBets.push(split[1])
		// })
		//
		// // Collect matched bets
		// matchedBets.push(
		// 	...this.betService.matchedBets$
		// 		.getValue()
		// 		.filter((bet) => bet.betId !== currentBetId)
		// 		.map((bet) => ({
		// 			...bet,
		// 			marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
		// 			dfOdds: bet.provider === MarketProvider.BF && this.helperService.isLineBet(bet) ? bet.averagePriceMatched : bet.dfOdds,
		// 			rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
		// 		})),
		// )
		//
		// // Collect unmatched bets
		// unmatchedBets.push(
		// 	...this.betService.unmatchedBets$
		// 		.getValue()
		// 		.filter((bet) => bet.betId !== currentBetId)
		// 		.map((bet) => ({
		// 			...bet,
		// 			marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
		// 			rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
		// 		})),
		// )
		//
		// // Consider lapsed bets as unmatched bets
		// unmatchedBets.push(
		// 	...this.betService.lapsedNonPendingBets$
		// 		.getValue()
		// 		.filter((bet) => bet.betId !== currentBetId)
		// 		.map((bet) => ({
		// 			...bet,
		// 			marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
		// 			rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
		// 		})),
		// )

		// Separate ODDS and LINE matched markets
		// const matchedOddsBets: CalculationBet[] = []
		// const matchedAsianHandicapBets: CalculationBet[] = []
		// const matchedGoalLinesBets: CalculationBet[] = []
		// const matchedDoubleChanceMatchedBets: CalculationBet[] = []
		// const matchedLineBets: CalculationBet[] = []
		//
		// matchedBets.forEach((bet) => {
		// 	if (this.helperService.isAsianHandicapBet(bet)) {
		// 		matchedAsianHandicapBets.push(bet)
		// 	} else if (this.helperService.isGoalLinesBet(bet)) {
		// 		matchedGoalLinesBets.push(bet)
		// 	} else if (this.helperService.isLineBet(bet)) {
		// 		matchedLineBets.push(bet)
		// 	} else if (this.helperService.isDoubleChanceBet(bet)) {
		// 		matchedDoubleChanceMatchedBets.push(bet)
		// 	} else {
		// 		matchedOddsBets.push(bet)
		// 	}
		// })
		//
		// // Insert the predicted bet in the appropriate group
		// if (this.helperService.isAsianHandicapBet(predictedBet)) {
		// 	matchedAsianHandicapBets.push(this.betService.enhancePredictedBetToCalculationBet(predictedBet))
		// } else if (this.helperService.isGoalLinesBet(predictedBet)) {
		// 	matchedGoalLinesBets.push(this.betService.enhancePredictedBetToCalculationBet(predictedBet))
		// } else if (this.helperService.isLineBet(predictedBet)) {
		// 	matchedLineBets.push(this.betService.enhancePredictedBetToCalculationBet(predictedBet))
		// } else if (this.helperService.isDoubleChanceBet(predictedBet)) {
		// 	matchedDoubleChanceMatchedBets.push(this.betService.enhancePredictedBetToCalculationBet(predictedBet))
		// } else {
		// 	matchedOddsBets.push(this.betService.enhancePredictedBetToCalculationBet(predictedBet))
		// }
		//
		// // Get unique market IDs and predictedBet's market ID from the bets
		// const matchedOddsBetsMarketGroupIds = Array.from(new Set(matchedOddsBets.map((bet) => bet.marketGroupId)))
		// const matchedAsianHandicapBetsMarketGroupIds = Array.from(new Set(matchedAsianHandicapBets.map((bet) => bet.marketGroupId)))
		// const matchedGoalLinesBetsMarketGroupIds = Array.from(new Set(matchedGoalLinesBets.map((bet) => bet.marketGroupId)))
		// const matchedDoubleChanceBetsMarketGroupIds = Array.from(new Set(matchedDoubleChanceMatchedBets.map((bet) => bet.marketGroupId)))
		// const matchedLineBetsUniqueIds = Array.from(new Set(matchedLineBets.map((bet) => `${bet.marketId}-${bet.selectionId}`)))
		//
		// matchedOddsBetsMarketGroupIds.forEach(
		// 	(marketGroupId) => (oddsExposuresStepThree[marketGroupId] = this.calculateOddsAndLineMarketBasedExposure(matchedOddsBets, marketGroupId)),
		// )
		// matchedAsianHandicapBetsMarketGroupIds.forEach(
		// 	(marketGroupId) =>
		// 		(asianHandicapExposuresStepThree[marketGroupId] = this.calculateAsianHandicapBasedExposure(matchedAsianHandicapBets, marketGroupId)),
		// )
		// matchedGoalLinesBetsMarketGroupIds.forEach(
		// 	(marketGroupId) => (goalLinesExposuresStepThree[marketGroupId] = this.calculateGoalLinesBasedExposure(matchedGoalLinesBets, marketGroupId)),
		// )
		// matchedDoubleChanceBetsMarketGroupIds.forEach(
		// 	(marketGroupId) => (doubleChanceExposuresStepThree[marketGroupId] = this.calculateDoubleChanceBasedExposure(matchedDoubleChanceMatchedBets)),
		// )
		// matchedLineBetsUniqueIds.forEach((uniqueId) => (lineExposuresStepThree[uniqueId] = this.calculateRunnerBasedExposure(matchedLineBets, uniqueId)))
		//
		// // Sum all the exposures got from the step above (This will be the total exposure)
		// const matchedOddsBetsTotalExposure = Object.values(oddsExposuresStepThree).reduce((prev, curr) => prev + curr, 0)
		// const matchedAsianHandicapBetsTotalExposure = Object.values(asianHandicapExposuresStepThree).reduce((prev, curr) => prev + curr, 0)
		// const matchedGoalLinesBetsTotalExposure = Object.values(goalLinesExposuresStepThree).reduce((prev, curr) => prev + curr, 0)
		// const matchedDoubleChanceBetsTotalExposure = Object.values(doubleChanceExposuresStepThree).reduce((prev, curr) => prev + curr, 0)
		// const matchedLineBetsTotalExposure = Object.values(lineExposuresStepThree).reduce((prev, curr) => prev + curr, 0)
		// const matchedBetsTotalExposure =
		// 	matchedOddsBetsTotalExposure +
		// 	matchedDoubleChanceBetsTotalExposure +
		// 	matchedLineBetsTotalExposure +
		// 	matchedAsianHandicapBetsTotalExposure +
		// 	matchedGoalLinesBetsTotalExposure +
		// 	this.calculateUnmatchedExposure(unmatchedBets)
		//
		// if (shouldDebug) {
		// 	let newMarketExposure
		// 	if (this.helperService.isAsianHandicapBet(predictedBet)) {
		// 		newMarketExposure = asianHandicapExposuresStepOne[predictedBet.marketGroupId]
		// 	} else if (this.helperService.isGoalLinesBet(predictedBet)) {
		// 		newMarketExposure = goalLinesExposuresStepOne[predictedBet.marketGroupId]
		// 	} else if (this.helperService.isLineBet(predictedBet)) {
		// 		newMarketExposure = lineExposuresStepThree[`${predictedBet.marketId}-${predictedBet.selectionId}`]
		// 	} else if (this.helperService.isDoubleChanceBet(predictedBet)) {
		// 		newMarketExposure = doubleChanceExposuresStepThree[predictedBet.marketGroupId]
		// 	} else {
		// 		newMarketExposure = oddsExposuresStepThree[predictedBet.marketGroupId]
		// 	}
		//
		// 	this.debuggingService.exposureDebuggingObject[debuggingKey] = {
		// 		newTotalExposure: matchedBetsTotalExposure,
		// 		newMarketExposure,
		// 	}
		// }
		//
		// // If validation fails, return the error
		// const matchedBetValidation = this.checkInsufficientBalance(matchedBetsTotalExposure, balanceData)
		// if (matchedBetValidation) {
		// 	return matchedBetValidation
		// }
		//
		// return null
	}

	/**
	 * Calculates the exposure for ODDS and LINE market bets.
	 *
	 * @param {CalculationBet[]} bets - Array of bet objects to evaluate.
	 * @param {string} marketGroupId - The identifier of the market group to filter the bets.
	 * @return {number} The absolute value of the minimum profit or loss for all runners within the market.
	 */
	private calculateOddsAndLineMarketBasedExposure(bets: CalculationBet[], marketGroupId: string): number {
		// Return zero for empty bets array
		if (bets.length === 0) return 0

		// Filter bets only within that market
		const betsOnlyWithinMarket = this.betService.getBetsOnlyWithinMarket(bets, marketGroupId)

		// Get the list of runners withing the market
		const market = this.betService.betsMarkets$.getValue()[betsOnlyWithinMarket[0]?.marketId]
		const runners = market?.runners

		// Calculate exposures for each runner and take minimum
		if (runners && runners.length > 0) {
			const marketExposures = runners.map((runner) =>
				this.calculateRunnerProfitOrLoss(betsOnlyWithinMarket, runner.selectionId, this.helperService.isLineMarket(market)),
			)

			return Math.abs(Math.min(...marketExposures, 0))
		}

		return 0
	}

	/**
	 * Calculates the exposure for Asian Handicap bets.
	 *
	 * @param {CalculationBet[]} bets - The list of bets to calculate the exposure for.
	 * @param {string} marketGroupId - The unique ID of the market group to which the bets belong.
	 * @return {number} The calculated exposure value based on Asian Handicap rules. Returns 0 if no relevant market or bets are found.
	 */
	calculateAsianHandicapBasedExposure(bets: CalculationBet[], marketGroupId: string): number {
		const betsOnlyWithinMarket = this.betService.getBetsOnlyWithinMarket(bets, marketGroupId)

		const market = this.betService.betsMarkets$.getValue()[betsOnlyWithinMarket[0]?.marketId]

		if (market?.runners) {
			const marketPnLs = this.asianHandicapService.determineACHPnL(market, betsOnlyWithinMarket)

			const marketExposures: number[] = marketPnLs.map((exposure) => exposure.pnls?.map((pnl: { pnl: number }) => pnl.pnl).flat()).flat()

			return Math.abs(Math.min(...marketExposures, 0))
		}

		return 0
	}

	/**
	 * Calculates exposure for Goal Lines bets.
	 *
	 * @param {CalculationBet[]} bets - The list of bets to calculate exposure for.
	 * @param {string} marketGroupId - The unique ID of the market group for which the exposure should be calculated.
	 * @return {number} The calculated exposure based on the goal lines, or 0 if no valid market data is available.
	 */
	calculateGoalLinesBasedExposure(bets: CalculationBet[], marketGroupId: string): number {
		const betsOnlyWithinMarket = this.betService.getBetsOnlyWithinMarket(bets, marketGroupId)

		const market = this.betService.betsMarkets$.getValue()[betsOnlyWithinMarket[0]?.marketId]

		if (market?.runners) {
			const marketPnLs = this.asianHandicapService.determineGLPnL(market, betsOnlyWithinMarket)

			const marketExposures: number[] = marketPnLs.map((exposure) => exposure.pnls?.map((pnl: { pnl: number }) => pnl.pnl).flat()).flat()

			return Math.abs(Math.min(...marketExposures, 0))
		}

		return 0
	}

	/**
	 * Calculates the exposure for a specific runner based on the provided bets and market-selection identifier.
	 *
	 * @param {CalculationBet[]} bets - The list of bets to be evaluated.
	 * @param {string} marketIdSelectionIdConcatenated - A concatenated string of market ID and selection ID, separated by a hyphen.
	 * @return {number} The calculated runner-based exposure as a non-negative number.
	 */
	calculateRunnerBasedExposure(bets: CalculationBet[], marketIdSelectionIdConcatenated: string): number {
		const betsOnlyWithinRunner = bets
			.filter(
				(bet) =>
					bet.marketId === marketIdSelectionIdConcatenated.split('-')[0] && bet.selectionId === Number(marketIdSelectionIdConcatenated.split('-')[1]),
			)
			.map((bet) => ({
				...bet,
				side: bet.provider === MarketProvider.BF ? bet.side : bet.side === 'BACK' ? 'LAY' : 'BACK',
				stake: Number(bet.betSize),
			}))
		const ladderValues = this.betService.calculateLadderRangesAndValues(betsOnlyWithinRunner).values

		return Math.abs(Math.min(0, ...ladderValues))
	}

	/**
	 * Calculates the double chance-based exposure for a given set of bets.
	 *
	 * @param {CalculationBet[]} bets - The array of bets used to calculate the exposure. Each bet contains details such as selectionId, side, potentialProfit, potentialLoss, and betSize.
	 * @return {number} The absolute value of the minimum exposure across home, draw, and away conditions. A greater exposure signifies higher potential liability.
	 */
	calculateDoubleChanceBasedExposure(bets: CalculationBet[]): number {
		let home = 0
		let draw = 0
		let away = 0

		for (const bet of bets) {
			switch (bet.selectionId) {
				case 6384648:
					if (bet.side === 'BACK') {
						home += Number(bet.potentialProfit)
						draw -= Number(bet.betSize)
						away += Number(bet.potentialProfit)
					} else {
						// LAY
						home -= Number(bet.potentialLoss)
						draw += Number(bet.betSize)
						away -= Number(bet.potentialLoss)
					}
					break
				case 6384647:
					if (bet.side === 'BACK') {
						home -= Number(bet.betSize)
						draw += Number(bet.potentialProfit)
						away += Number(bet.potentialProfit)
					} else {
						// LAY
						home += Number(bet.betSize)
						draw -= Number(bet.potentialLoss)
						away -= Number(bet.potentialLoss)
					}
					break
				case 6384646:
					if (bet.side === 'BACK') {
						home += Number(bet.potentialProfit)
						draw += Number(bet.potentialProfit)
						away -= Number(bet.betSize)
					} else {
						// LAY
						home -= Number(bet.potentialLoss)
						draw -= Number(bet.potentialLoss)
						away += Number(bet.betSize)
					}
					break
				default:
					break
			}
		}

		return Math.abs(Math.min(0, home, draw, away))
	}

	/**
	 * Calculates the total unmatched exposure by summing the potential loss of all bets.
	 *
	 * @param {CalculationBet[]} bets - An array of CalculationBet objects where each contains a potentialLoss property.
	 * @return {number} The total unmatched exposure calculated by summing the potential losses.
	 */
	private calculateUnmatchedExposure(bets: CalculationBet[]): number {
		return bets.reduce((prev, curr) => prev + Number(curr.potentialLoss), 0)
	}

	/**
	 * Calculates the available balance that can be used for placing bets.
	 * The calculation is based on the provided balance data, where:
	 * Available to Bet (ATB) = Balance (B) + (Total Exposure (TE) - Liability (L)).
	 *
	 * @param {BalanceData} balanceData - The balance data object containing balance, total exposure, and liability information.
	 * @return {number} The amount available to bet, calculated using the provided balance data.
	 */
	private getAvailableToBet(balanceData: BalanceData): number {
		// Available to bet(ATB) = Balance(B) + (Total Exposure(TE) - Liability(L))
		return Number(balanceData.BALANCE) + Number(balanceData.TOTAL_EXPOSURE) - Number(balanceData.LIABILITY ?? '0')
	}

	/**
	 * Checks if the balance is not enough compared to the total exposure.
	 *
	 * @param {number} totalExposure - The total exposure to compare against the balance.
	 * @param {BalanceData} balanceData - The balance data containing the balance value.
	 * @return {Object|null} Returns an object with an insufficientBalance error if the balance is less than totalExposure, otherwise returns null.
	 */
	private checkInsufficientBalance(totalExposure: number, balanceData: BalanceData): { insufficientBalance: boolean } | null {
		// If balance is less than totalExposure return insufficient balance validation error else return null.
		return Number(balanceData.BALANCE) < totalExposure ? { insufficientBalance: true } : null
	}

	/**
	 * Calculate the profit or loss value
	 * @param bets
	 * @param selectionId
	 * @param isLineMarket
	 */
	private calculateRunnerProfitOrLoss(bets: CalculationBet[], selectionId: number, isLineMarket: boolean) {
		if (isLineMarket) {
			// This is needed because for Fancy markets, selection ID is what makes them unique. This also works for BF LINE markets
			const betForTheRunner = bets.filter((bet) => bet.selectionId === selectionId)
			return Math.min(
				...this.betService.calculateLadderRangesAndValues(betForTheRunner.map((bet) => ({ ...bet, stake: Number(bet.betSize), rangeOdd: bet.dfOdds })))
					.values,
			)
		}

		const mySelectionBets = bets.filter((bet) => bet.selectionId === selectionId)
		const otherSelectionsBets = bets.filter((bet) => bet.selectionId !== selectionId)

		if (mySelectionBets.length === 0 && otherSelectionsBets.length === 0) {
			return 0
		}

		const currentSelectionProfits: number[] = []
		mySelectionBets.forEach((bet) => {
			if (bet.side === 'BACK') {
				currentSelectionProfits.push(Number(bet.potentialProfit))
			}
		})
		otherSelectionsBets.forEach((bet) => {
			if (bet.side === 'LAY') {
				currentSelectionProfits.push(Number(bet.potentialProfit))
			}
		})

		const currentSelectionLosses: number[] = []
		mySelectionBets.forEach((bet) => {
			if (bet.side === 'LAY') {
				currentSelectionLosses.push(Number(bet.potentialLoss))
			}
		})
		otherSelectionsBets.forEach((bet) => {
			if (bet.side === 'BACK') {
				currentSelectionLosses.push(Number(bet.potentialLoss))
			}
		})

		const mySelectionProfit = currentSelectionProfits.reduce((prev, current) => prev + current, 0)
		const mySelectionLoss = currentSelectionLosses.reduce((prev, current) => prev + current, 0)

		return mySelectionProfit - mySelectionLoss
	}

	private checkWorstCaseExposure(worstCaseUnmatchedBets: CalculationBet[], consideredMatchedBets: CalculationBet[], balanceData: BalanceData): boolean {
		const allBetsForRiskService = [
			...consideredMatchedBets.map((bet) => ({
				betId: bet.betId,
				marketId: bet.marketId,
				selectionId: bet.selectionId.toString(),
				side: bet.side.toLowerCase() as 'back' | 'lay',
				price: bet.betId === '-1' ? Number(bet.fOdds) : Number(bet.averagePriceMatched) || Number(bet.fOdds) || 1.01,
				betSize: Number(bet.betSize),
				stakeMatched: Number(bet.stakeMatched),
			})),
			...worstCaseUnmatchedBets.map((bet) => ({
				betId: bet.betId,
				marketId: bet.marketId,
				selectionId: bet.selectionId.toString(),
				side: bet.side.toLowerCase() as 'back' | 'lay',
				price: bet.betId === '-1' ? Number(bet.fOdds) : Number(bet.averagePriceMatched) || Number(bet.fOdds) || 1.01,
				betSize: Number(bet.betSize),
				stakeMatched: 0,
			})),
		]

		const marketSelectionIds: Map<string, string[]> = this.betService.getMarketSelectionIds(allBetsForRiskService)
		const worstExposure = this.riskExposureService.calculateExposure(allBetsForRiskService, marketSelectionIds)
		return worstExposure > +balanceData.BALANCE
	}

	private enhanceBetToCalculationBet(bet: Bet, betSize: string | number): CalculationBet {
		return {
			...bet,
			marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
			potentialProfit: bet.potentialProfit,
			potentialLoss: bet.potentialLoss,
			betSize: betSize,
			averagePriceMatched: bet.averagePriceMatched,
			rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
		}
	}
}
